import { PromoDetail } from "@/core/entities/promotion/PromoDetail";
import { Flex, Span } from "@ir/ui";
import { memo } from "react";
import { ProgramInfoCard } from "./ProgramInfoCard";
import { PromoDetailInfoRow } from "./PromoDetailInfoRow";
import { getEndDateClassName } from "../utils/promotionDisplay.utils";
import EditProgramDetails from "./edit/EditProgramDetails";

interface ProgramDetailsCardProps {
  promoDetail: PromoDetail;
  dateFormat: string;
  numberFormat: string;
  isEditingDetails: boolean;
}

export const ProgramDetailsCard = memo(
  ({ promoDetail, dateFormat, numberFormat, isEditingDetails }: ProgramDetailsCardProps) => {
    const endDateClassName = getEndDateClassName(promoDetail.isEndDatePast(), promoDetail.isEndDateNear());

    return (
      <ProgramInfoCard title="Promotion Details">
        {isEditingDetails ? (
          <EditProgramDetails promoDetail={promoDetail} />
        ) : (
          <>
            <PromoDetailInfoRow label="Promo type" value={promoDetail.type.promoTypeName || "-"} />
            <PromoDetailInfoRow label="Purchaser" value={promoDetail.purchaser.purchaserName || "-"} />
            <PromoDetailInfoRow label="Retailer" value={promoDetail.retailer?.retailerName || "-"} />
            <PromoDetailInfoRow label="Start date" value={promoDetail.formatDate(promoDetail.startDate, dateFormat)} />
            <PromoDetailInfoRow
              label="End date"
              value={promoDetail.formatDate(promoDetail.endDate, dateFormat)}
              valueClassName={endDateClassName}
            />
            <PromoDetailInfoRow label="Amount tolerance" value={promoDetail.amountTolerance ?? "-"} />
            <PromoDetailInfoRow label="Date tolerance" value={promoDetail.dateTolerance ?? "-"} />
            <PromoDetailInfoRow
              label="Budget"
              value={promoDetail.formatNumber(promoDetail.financials.committedBudget, numberFormat)}
            />
            <PromoDetailInfoRow
              label="Claimed"
              value={
                promoDetail?.status?.promoStatusKey === "DRAFT"
                  ? "-"
                  : promoDetail.formatNumber(promoDetail.financials.previouslyClaimed, numberFormat)
              }
            />
            <PromoDetailInfoRow
              label="Open balance"
              value={
                promoDetail?.status?.promoStatusKey === "DRAFT"
                  ? "-"
                  : promoDetail.formatNumber(promoDetail.financials.remainingOpenBalance, numberFormat)
              }
              valueClassName={
                promoDetail?.status?.promoStatusKey === "DRAFT"
                  ? "text-grey-700"
                  : promoDetail.financials.remainingOpenBalance && promoDetail.financials.remainingOpenBalance < 0
                    ? "text-error-600"
                    : "text-grey-700"
              }
            />
            <Flex
              align="start"
              justifyContent="space-between"
              wrap="no-wrap"
              gap={2}
              className="promo-detail-info-row py-1 border-bottom-0 px-4"
            >
              <Span className="text-grey-500 text-sm shrink-0 mr-3" style={{ flex: 1 }}>
                Notes
              </Span>
              <Span className="text-sm font-medium text-grey-700 text-right" style={{ flex: 3 }}>
                {promoDetail.notes ?? "-"}
              </Span>
            </Flex>
          </>
        )}
      </ProgramInfoCard>
    );
  }
);

ProgramDetailsCard.displayName = "ProgramDetailsCard";
